import { useState } from "react";
import { api, formatDate } from "@/lib/api";
import {
  CheckCircle, Circle, Warning, X, PencilSimple, ArrowClockwise,
  Phone, Handshake, FileText, House, Bank, ShieldCheck, FileArrowDown,
  Confetti, CurrencyEur, Calendar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { JalonActions } from "@/components/JalonActions";

/* ============================================================
 * TIMELINE — 10 étapes opérationnelles
 * ============================================================
 *   1. Prospect           (created_at)             — no prevision
 *   2. Rendez-vous        (rdv_fait_date)          — no prevision
 *   3. Signature mandat   (mandat_signe_date)      — no prevision
 *   4. Compromis          (compromis_date)         — ancre des prévisions
 *   5. En banque          (etape_dates.envoi_banque)        prev = compromis +30j
 *   6. Accord banque      (etape_dates.accord_banque)       prev = compromis +40j
 *   7. Accord risque      (etape_dates.accord_risque)       prev = compromis +50j
 *   8. Offre éditée       (etape_dates.offre_editee)        prev = compromis +60j
 *   9. Acte authentique   (etape_dates.acte_authentique)    prev = compromis +90j
 *  10. Encaissement       (encaissement_date)               prev = mois acte + 1
 *
 *  - Pré-compromis : pas de prévision (visuel "—")
 *  - Post-compromis : delta réel vs prévisionnel en jours (vert ≤ obj, rouge > obj)
 *  - Compromis = ancre. Dès qu'elle est posée, toutes les prévisions sont auto.
 * ============================================================ */

const STAGES_BACKEND = ["prospect", "compromis", "envoi_banque", "accord_banque", "accord_risque", "rdv_bancaire", "offre_editee", "acte_authentique"];

function stageBackendIndex(key) {
  const i = STAGES_BACKEND.indexOf(key);
  return i < 0 ? 0 : i;
}

function diffDays(aIso, bIso) {
  if (!aIso || !bIso) return null;
  try {
    const a = new Date(aIso + "T00:00:00");
    const b = new Date(bIso + "T00:00:00");
    return Math.round((a - b) / 86400000);
  } catch { return null; }
}

function DeltaBadge({ delta }) {
  if (delta === null || delta === undefined) return null;
  const isLate = delta > 0;
  const isEarly = delta < 0;
  const color = isLate ? "var(--cf-danger-text)" : isEarly ? "var(--cf-success-text)" : "var(--cf-text-2)";
  const bg = isLate ? "var(--cf-danger-soft)" : isEarly ? "var(--cf-success-soft)" : "var(--cf-bg-2)";
  const label = delta === 0 ? "à l'heure" : `${delta > 0 ? "+" : ""}${delta}j`;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-bold tabular-nums"
      style={{ background: bg, color }}
      data-testid="timeline-delta"
    >
      {label}
    </span>
  );
}

/* ---------- Inline date editor (iOS-friendly) ----------
 * Stratégie : input date TOUJOURS rendu, transparent et placé en surcouche
 * sur le label visible. Le tap natif déclenche directement le picker iOS sans
 * passer par showPicker() (qui exige un gesture-chain synchrone que iOS casse).
 * Touch target élargi à 44px min (HIG Apple). */
function InlineDate({ value, onSave, allowClear = true, allowFuture = false, testid, placeholder = "définir" }) {
  const today = new Date().toISOString().slice(0, 10);
  const inputId = `${testid || "inline-date"}-input`;

  // Mac/Desktop Chrome+Safari : click natif sur <input type=date> n'ouvre pas
  // le picker automatiquement. Il faut appeler showPicker() de façon synchrone
  // dans un handler user-initiated. iOS reste OK (htmlFor + tap natif).
  const forceOpen = (e) => {
    const input = document.getElementById(inputId);
    if (!input) return;
    try {
      input.showPicker?.();
    } catch { /* showPicker unsupported on older Safari → fallback focus */
      try { input.focus(); } catch { /* noop */ }
    }
  };

  return (
    <span
      className="relative inline-flex items-center gap-1 group"
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
    >
      {/* Label visible — sert aussi de touch target élargi (min-h 28px) */}
      <label
        htmlFor={inputId}
        onClick={(e) => {
          // Sur desktop Chrome/Safari : forcer l'ouverture du picker.
          // On preventDefault pour éviter le double-trigger (label → input click natif).
          e.preventDefault();
          forceOpen(e);
        }}
        className="inline-flex items-center gap-1 text-[13px] font-semibold text-[color:var(--cf-primary)] hover:bg-blue-50 rounded px-1.5 py-1 transition-colors cursor-pointer select-none min-h-[28px]"
      >
        <span className={value ? "" : "underline decoration-dotted underline-offset-2"}>
          {value ? formatDate(value) : placeholder}
        </span>
        <PencilSimple size={10} weight="bold" className="opacity-0 group-hover:opacity-60 transition-opacity" />
      </label>

      {/* Input date natif transparent — overlapping → tap iOS-safe */}
      <input
        id={inputId}
        type="date"
        value={value || ""}
        max={allowFuture ? undefined : today}
        onChange={(e) => onSave(e.target.value || null)}
        onClick={forceOpen}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        style={{ minHeight: 28 }}
        aria-label={placeholder}
        data-testid={`${testid}-native`}
      />

      {/* Bouton effacer — séparé pour ne pas être recouvert par l'input */}
      {value && allowClear && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSave(null); }}
          className="relative z-10 w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"
          title="Effacer"
          data-testid={`${testid}-clear`}
        >
          <X size={11} weight="bold" />
        </button>
      )}
    </span>
  );
}

/* ---------- Step row ---------- */
function Step({
  Icon, label, idx, total,
  realDate, prevDate, prevAuto,
  isDone, isFuture, accentColor = "var(--cf-primary)",
  onSaveReal, onSavePrev, onResetPrev,
  realTestid, prevTestid,
  allowClearReal = true,
  realPlaceholder,
  readOnlyReal = false,
  onCorrect, // callback to unlock readOnly with explicit confirm
  allowFutureReal = false, statusBadge = null, extraActions = null,
  jalon = null, // V9-A — { dossier, jalonKey, onUpdated }
}) {
  const delta = diffDays(realDate, prevDate);

  let dotBg = "#FFFFFF";
  let dotBorder = "var(--cf-border)";
  let dotIconColor = "var(--cf-text-3)";
  if (isDone) { dotBg = accentColor; dotBorder = accentColor; dotIconColor = "#FFFFFF"; }
  if (isFuture) { dotBg = "#FFFFFF"; dotBorder = "var(--cf-border)"; dotIconColor = "var(--cf-border-strong)"; }

  const labelColor = isFuture ? "var(--cf-text-3)" : (isDone ? "var(--cf-text)" : "var(--cf-text)");

  return (
    <div className="flex items-start gap-3.5 py-3 relative" data-testid={`timeline-step-${idx}`}>
      {/* vertical line */}
      {idx < total - 1 && (
        <span
          aria-hidden
          className="absolute left-[17px] top-[42px] bottom-[-12px] w-[2px]"
          style={{ background: isDone ? accentColor : "var(--cf-border)" }}
        />
      )}

      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 z-10"
        style={{ background: dotBg, borderColor: dotBorder, borderWidth: isFuture ? 2 : 0 }}
      >
        <Icon size={17} weight={isDone ? "fill" : "regular"} color={dotIconColor} />
      </div>

      <div className="flex-1 min-w-0 pt-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="font-bold text-[14px] leading-tight" style={{ color: labelColor }}>
            {label}
          </span>
          <DeltaBadge delta={delta} />
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {readOnlyReal ? (
            <>
              <span className="text-[13px] font-semibold text-slate-700" data-testid={realTestid}>
                {realDate ? formatDate(realDate) : "—"}
              </span>
              {onCorrect && (
                <button
                  type="button"
                  onClick={onCorrect}
                  className="text-[10.5px] font-bold text-slate-400 hover:text-[color:var(--cf-danger-text)] inline-flex items-center gap-0.5 underline decoration-dotted underline-offset-2"
                  title="Modification exceptionnelle — impacte les stats de performance"
                  data-testid={`${realTestid}-correct`}
                >
                  Corriger
                </button>
              )}
            </>
          ) : (
            <InlineDate
              value={realDate}
              onSave={onSaveReal}
              allowClear={allowClearReal}
              allowFuture={allowFutureReal}
              testid={realTestid}
              placeholder={realPlaceholder || "définir"}
            />
          )}
          {statusBadge}
          {extraActions}
          {jalon && <div className="w-full basis-full"><JalonActions {...jalon} /></div>}
          {(prevDate || onSavePrev) && !isDone && (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 font-semibold">
              <Calendar size={10} weight="fill" /> Prév.
              {onSavePrev ? (
                <InlineDate
                  value={prevDate}
                  onSave={onSavePrev}
                  allowClear={false}
                  allowFuture={true}
                  testid={prevTestid}
                  placeholder="—"
                />
              ) : (
                <span className="text-slate-500">{prevDate ? formatDate(prevDate) : "—"}</span>
              )}
              {onResetPrev && !prevAuto && (
                <button
                  type="button"
                  onClick={onResetPrev}
                  className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[color:var(--cf-primary)] hover:underline"
                  title="Réinitialiser auto"
                  data-testid={`${prevTestid}-reset`}
                >
                  <ArrowClockwise size={10} weight="bold" /> auto
                </button>
              )}
              {onSavePrev && prevAuto && (
                <span className="text-[9.5px] font-bold text-slate-300 uppercase tracking-wider">auto</span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Main component ---------- */
export default function DossierTimeline({ dossier, onUpdated }) {
  const d = dossier;
  const dates = d.etape_dates || {};
  const currentIdx = stageBackendIndex(d.etape);
  const hasMandat = !!d.mandat_signe;
  const hasCompromis = !!d.compromis_date;
  const status = d.status;

  // États locaux pour les corrections exceptionnelles
  const [prospectUnlocked, setProspectUnlocked] = useState(false);
  const [prospectConfirmOpen, setProspectConfirmOpen] = useState(false);

  const saveCompromisField = async (patch) => {
    try {
      const res = await api.patch(`/dossiers/${d.id}/compromis`, patch);
      onUpdated?.(res.data);
      toast.success("Mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const saveStageDate = async (etapeKey, dateIso) => {
    try {
      const res = await api.patch(`/dossiers/${d.id}/etape-date`, { etape: etapeKey, date: dateIso });
      onUpdated?.(res.data);
      toast.success(dateIso ? "Date mise à jour" : "Date supprimée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const rdvAction = async (action, date) => {
    if (action === "complete" && d.etape_dates?.rdv_bancaire > new Date().toISOString().slice(0, 10)) {
      toast.error("Un RDV ne peut pas être réalisé dans le futur — reporte-le d'abord à la bonne date");
      return;
    }
    try {
      const res = await api.post(`/dossiers/${d.id}/rdv-bancaire`, { action, date });
      onUpdated(res.data);
      toast.success(action === "complete" ? "RDV bancaire marqué réalisé" : action === "cancel" ? "RDV bancaire annulé (historique conservé)" : action === "postpone" ? "RDV bancaire reporté (historique conservé)" : "RDV bancaire planifié");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const savePatch = async (patch) => {
    try {
      const res = await api.patch(`/dossiers/${d.id}`, patch);
      onUpdated?.(res.data);
      toast.success("Mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // Correction exceptionnelle de la date Prospect
  const saveProspectDate = async (dateIso) => {
    try {
      const res = await api.patch(`/dossiers/${d.id}/prospect-date`, {
        date: dateIso,
        confirm: true,
      });
      onUpdated?.(res.data);
      toast.success(dateIso ? "Date prospect corrigée" : "Date prospect réinitialisée");
      setProspectUnlocked(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  // Build 10 steps
  const total = 10;
  const prospectIso = d.prospect_date || (d.created_at || "").slice(0, 10) || null;
  const steps = [
    {
      label: "Prospect", Icon: Phone, accentColor: "var(--cf-text-2)",
      realDate: prospectIso,
      prevDate: null, prevAuto: false,
      isDone: true, isFuture: false,
      // Verrouillé par défaut, déverrouillé après confirmation explicite
      readOnlyReal: !prospectUnlocked,
      onCorrect: !prospectUnlocked ? () => setProspectConfirmOpen(true) : null,
      onSaveReal: prospectUnlocked ? saveProspectDate : null,
      realTestid: "timeline-prospect-date",
      allowClearReal: !!d.prospect_date, // peut effacer override seulement
    },
    {
      label: "Rendez-vous", Icon: Handshake, accentColor: "var(--cf-primary)",
      realDate: d.rdv_fait_date || null,
      prevDate: null, prevAuto: false,
      isDone: !!d.rdv_fait_date || d.contact_status === "rdv_fait" || ["rdv_fait", "en_reflexion", "en_recherche", "offre_en_cours"].includes(d.type_contact),
      isFuture: false,
      onSaveReal: (v) => savePatch({ rdv_fait_date: v }),
      realPlaceholder: "définir",
      realTestid: "timeline-rdv-date",
    },
    {
      jalon: { dossier: d, jalonKey: "mandat_signe", onUpdated },
      label: "Signature du mandat", Icon: FileText, accentColor: "var(--cf-accent)",
      realDate: d.mandat_signe_date || null,
      prevDate: null, prevAuto: false,
      isDone: hasMandat,
      isFuture: false,
      // V4 : éditable librement (auto-rempli au toggle, modifiable ensuite)
      onSaveReal: (v) => savePatch({ mandat_signe_date: v }),
      realPlaceholder: hasMandat ? "définir" : "—",
      realTestid: "timeline-mandat-date",
    },
    {
      jalon: { dossier: d, jalonKey: "compromis", onUpdated },
      label: "Compromis", Icon: House, accentColor: "var(--cf-primary)",
      realDate: d.compromis_date || null,
      prevDate: null, prevAuto: false,
      isDone: hasCompromis,
      isFuture: false,
      onSaveReal: (v) => saveCompromisField({ compromis_date: v }),
      realTestid: "timeline-compromis-date",
    },
    {
      jalon: { dossier: d, jalonKey: "envoi_banque", onUpdated },
      label: "En banque", Icon: Bank, accentColor: "var(--cf-primary)",
      realDate: dates.envoi_banque || null,
      prevDate: d.envoi_banque_previsionnel_date || null,
      prevAuto: d.envoi_banque_previsionnel_auto !== false,
      isDone: currentIdx >= stageBackendIndex("envoi_banque") && !!dates.envoi_banque,
      isFuture: !hasCompromis,
      onSaveReal: (v) => saveStageDate("envoi_banque", v),
      onSavePrev: hasCompromis ? (v) => saveCompromisField({ envoi_banque_previsionnel_date: v }) : null,
      onResetPrev: hasCompromis ? () => saveCompromisField({ recompute_auto: true }) : null,
      realTestid: "timeline-envoi-banque-date",
      prevTestid: "timeline-envoi-banque-prev",
    },
    {
      jalon: { dossier: d, jalonKey: "accord_banque", onUpdated },
      label: "Accord banque", Icon: CheckCircle, accentColor: "var(--cf-success)",
      realDate: dates.accord_banque || null,
      prevDate: d.accord_banque_previsionnel_date || null,
      prevAuto: d.accord_banque_previsionnel_auto !== false,
      isDone: !!dates.accord_banque,
      isFuture: !hasCompromis,
      onSaveReal: (v) => saveStageDate("accord_banque", v),
      onSavePrev: hasCompromis ? (v) => saveCompromisField({ accord_banque_previsionnel_date: v }) : null,
      onResetPrev: hasCompromis ? () => saveCompromisField({ recompute_auto: true }) : null,
      realTestid: "timeline-accord-banque-date",
      prevTestid: "timeline-accord-banque-prev",
    },
    {
      jalon: { dossier: d, jalonKey: "accord_risque", onUpdated },
      label: "Accord risque", Icon: ShieldCheck, accentColor: "var(--cf-success)",
      realDate: dates.accord_risque || null,
      prevDate: d.accord_risque_previsionnel_date || null,
      prevAuto: d.accord_risque_previsionnel_auto !== false,
      isDone: !!dates.accord_risque,
      isFuture: !hasCompromis,
      onSaveReal: (v) => saveStageDate("accord_risque", v),
      onSavePrev: hasCompromis ? (v) => saveCompromisField({ accord_risque_previsionnel_date: v }) : null,
      onResetPrev: hasCompromis ? () => saveCompromisField({ recompute_auto: true }) : null,
      realTestid: "timeline-accord-risque-date",
      prevTestid: "timeline-accord-risque-prev",
    },
    // V4.46 — RDV bancaire positionné APRÈS Accord risque (réalité métier validée user) :
    // Compromis → En banque → Accord banque → Accord risque → RDV bancaire → Offre éditée
    {
      jalon: { dossier: d, jalonKey: "rdv_bancaire", onUpdated },
      label: "RDV bancaire", Icon: Bank, accentColor: "var(--cf-accent)",
      realDate: dates.rdv_bancaire || null,
      prevDate: null,
      prevAuto: false,
      // Pipe BOS : une date = PLANIFIÉ ; RÉALISÉ uniquement par action explicite (jamais implicite)
      isDone: d.rdv_bancaire_status === "completed",
      isFuture: !hasCompromis,
      allowFutureReal: true,
      onSaveReal: (v) => saveStageDate("rdv_bancaire", v),
      statusBadge: d.rdv_bancaire_status ? (
        <span data-testid="timeline-rdv-bancaire-status" className={`text-[9.5px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded ${d.rdv_bancaire_status === "completed" ? "bg-emerald-100 text-emerald-800" : d.rdv_bancaire_status === "cancelled" ? "bg-slate-200 text-slate-600" : "bg-violet-100 text-violet-800"}`}>
          {d.rdv_bancaire_status === "completed" ? "Réalisé" : d.rdv_bancaire_status === "cancelled" ? "Annulé" : "Planifié"}
        </span>
      ) : null,
      extraActions: (d.rdv_bancaire_status === "planned" || d.rdv_bancaire_status === "cancelled") ? (
        <span className="inline-flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
          {d.rdv_bancaire_status === "planned" && (
            <button type="button" onClick={() => rdvAction("complete")} className="text-[10px] font-bold text-emerald-700 hover:underline" data-testid="timeline-rdv-bancaire-complete">Marquer réalisé</button>
          )}
          {d.rdv_bancaire_status === "planned" && (
            <button type="button" onClick={() => { const v = window.prompt("Nouvelle date du RDV bancaire (AAAA-MM-JJ)", d.etape_dates?.rdv_bancaire || ""); if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) rdvAction("postpone", v); else if (v) toast.error("Format attendu : AAAA-MM-JJ"); }} className="text-[10px] font-bold text-violet-700 hover:underline" data-testid="timeline-rdv-bancaire-postpone">Reporter</button>
          )}
          {d.rdv_bancaire_status === "planned" && (
            <button type="button" onClick={() => rdvAction("cancel")} className="text-[10px] font-bold text-slate-500 hover:underline" data-testid="timeline-rdv-bancaire-cancel">Annuler</button>
          )}
          {d.rdv_bancaire_status === "cancelled" && (
            <span className="text-[10px] text-slate-500 font-semibold">à replanifier (choisir une date)</span>
          )}
        </span>
      ) : null,
      realTestid: "timeline-rdv-bancaire-date",
    },
    {
      jalon: { dossier: d, jalonKey: "offre_editee", onUpdated },
      label: "Offre éditée", Icon: FileArrowDown, accentColor: "var(--cf-success)",
      realDate: dates.offre_editee || null,
      prevDate: d.offre_editee_previsionnel_date || null,
      prevAuto: d.offre_editee_previsionnel_auto !== false,
      isDone: !!dates.offre_editee,
      isFuture: !hasCompromis,
      onSaveReal: (v) => saveStageDate("offre_editee", v),
      onSavePrev: hasCompromis ? (v) => saveCompromisField({ offre_editee_previsionnel_date: v }) : null,
      onResetPrev: hasCompromis ? () => saveCompromisField({ recompute_auto: true }) : null,
      realTestid: "timeline-offre-editee-date",
      prevTestid: "timeline-offre-editee-prev",
    },
    {
      jalon: { dossier: d, jalonKey: "acte_authentique", onUpdated },
      label: "Acte authentique", Icon: Confetti, accentColor: "var(--cf-success)",
      realDate: dates.acte_authentique || null,
      prevDate: d.acte_previsionnel_date || null,
      prevAuto: d.acte_previsionnel_auto !== false,
      isDone: currentIdx >= stageBackendIndex("acte_authentique") && !!dates.acte_authentique,
      isFuture: !hasCompromis,
      onSaveReal: (v) => saveStageDate("acte_authentique", v),
      onSavePrev: hasCompromis ? (v) => saveCompromisField({ acte_previsionnel_date: v }) : null,
      onResetPrev: hasCompromis ? () => saveCompromisField({ recompute_auto: true }) : null,
      realTestid: "timeline-acte-date",
      prevTestid: "timeline-acte-prev",
    },
    {
      jalon: { dossier: d, jalonKey: "encaissement", onUpdated },
      label: "Encaissement", Icon: CurrencyEur, accentColor: "var(--cf-success-text)",
      realDate: d.encaissement_date || null,
      prevDate: null, prevAuto: false,
      isDone: !!d.encaissement_date,
      isFuture: !dates.acte_authentique && !d.acte_previsionnel_date,
      onSaveReal: (v) => savePatch({ encaissement_date: v }),
      realTestid: "timeline-encaissement-date",
      // Mois prévisionnel auto (réel acte prime sur prévisionnel) — affiché via realPlaceholder
      realPlaceholder: d.encaissement_previsionnel_month
        ? `prév. ${new Date(d.encaissement_previsionnel_month + "-01T00:00:00").toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`
        : "définir",
    },
  ];

  // Chronology conflicts (post-compromis only, on real dates)
  const conflicts = {};
  let prevRealDate = null;
  for (const s of steps) {
    if (s.realDate && prevRealDate && s.realDate < prevRealDate) {
      conflicts[s.label] = true;
    }
    if (s.realDate) prevRealDate = s.realDate;
  }

  const isLost = status === "perdu";

  return (
    <div className="card-base p-5 md:p-6 mb-4" data-testid="dossier-timeline">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900">
          Avancement du dossier
        </h2>
        {hasCompromis && (
          <button
            type="button"
            onClick={() => saveCompromisField({ recompute_auto: true })}
            className="text-[11px] font-bold text-slate-400 hover:text-[color:var(--cf-primary)] inline-flex items-center gap-1"
            title="Recalculer toutes les prévisions depuis le compromis"
            data-testid="timeline-recompute-all"
          >
            <ArrowClockwise size={11} weight="bold" /> Recalc. prévisions
          </button>
        )}
      </div>

      {!hasCompromis && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-[11.5px] font-semibold text-slate-500" data-testid="timeline-pre-compromis-hint">
          Pose la date de compromis pour activer les prévisions automatiques : +30j En banque, +40j Accord banque, +50j Accord risque, +2 mois Offre éditée, +3 mois Acte authentique.
        </div>
      )}

      <div className={`relative ${isLost ? "opacity-40 pointer-events-none" : ""}`}>
        {steps.map((s, idx) => (
          <Step key={s.label} {...s} idx={idx} total={total} />
        ))}
      </div>

      {Object.keys(conflicts).length > 0 && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-100 flex items-start gap-2" data-testid="timeline-chronology-warning">
          <Warning size={14} weight="fill" color="var(--cf-warning)" className="flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-[color:var(--cf-warning-text)] leading-snug">
            Incohérence chronologique sur&nbsp;: <strong>{Object.keys(conflicts).join(", ")}</strong>. Édition libre conservée — corrige quand tu peux pour fiabiliser les stats.
          </p>
        </div>
      )}

      {/* Confirm dialog : correction exceptionnelle date Prospect */}
      {prospectConfirmOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setProspectConfirmOpen(false)}
          data-testid="prospect-correct-overlay"
        >
          <div
            className="bg-white max-w-md w-full rounded-2xl shadow-2xl p-5 md:p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="prospect-correct-dialog"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[color:var(--cf-warning-soft)] flex items-center justify-center flex-shrink-0">
                <Warning size={22} weight="fill" color="var(--cf-warning-text)" />
              </div>
              <div>
                <div className="font-display text-lg font-extrabold text-slate-900 leading-tight">
                  Modifier la date de prospect ?
                </div>
                <div className="text-[13px] text-slate-600 mt-1 leading-snug">
                  Cette modification <strong>impactera les statistiques de performance</strong> (délais réels, deltas, preuves opérationnelles).
                </div>
              </div>
            </div>
            <div className="mt-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-[12px] text-slate-500 font-semibold">
              À utiliser uniquement pour : import historique, erreur Zapier, migration, mauvaise saisie.
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button
                type="button"
                onClick={() => setProspectConfirmOpen(false)}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] font-bold"
                data-testid="prospect-correct-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  setProspectUnlocked(true);
                  setProspectConfirmOpen(false);
                  toast.info("Date prospect déverrouillée — édite avec précaution");
                }}
                className="px-4 py-2 rounded-xl bg-[color:var(--cf-warning-text)] hover:bg-[color:var(--cf-warning-text)] text-white text-[13px] font-extrabold"
                data-testid="prospect-correct-confirm"
              >
                Déverrouiller
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
